// src/ui/VaultGate.jsx — the unlock screen, and the offer that creates one.
//
// 🟥 THE HONEST VERSION OF "ENCRYPTED AT REST". A key the browser can read
//    unattended is a key an attacker with the laptop can read too, so at-rest
//    encryption is only real once there is a secret the disk does not hold. That
//    is what this screen collects. Until somebody sets one, the caches stay in
//    the clear and the 24-hour wipe is the protection — which is stated plainly
//    below rather than implied away, because a person who thinks their laptop is
//    encrypted and is wrong is worse off than one who knows it is not.
import React, { useState } from 'react';
import { enrollWithPin, unlockWithPin } from '../crypto/vault';
import './pairing.css';

export default function VaultGate({ mode, onDone, onSkip }) {
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const enrolling = mode === 'enroll';

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr(''); setBusy(true);
    try {
      if (enrolling) {
        if (pin.length < 4) { setErr('Use at least 4 digits.'); return; }
        /* 🟥 CONFIRMED BEFORE IT PROTECTS ANYTHING. A mistyped PIN here is not a
           login someone can reset — it is the only key to data on this machine,
           and nothing else in the system can recover it. */
        if (pin !== confirm) { setErr('Those do not match.'); return; }
        await enrollWithPin(pin);
      } else if (!(await unlockWithPin(pin))) {
        /* 🟢 the tag failed, which is the only check there is — see vault.js */
        setErr('That is not the right code.');
        setPin('');
        return;
      }
      onDone?.();
    } catch (e2) {
      setErr(e2?.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vault-gate">
      <form className="vg-card" onSubmit={submit}>
        <h2>{enrolling ? 'Lock this browser' : 'Enter your code'}</h2>
        <p className="vg-sub">
          {enrolling
            ? 'Your messages and media are stored on this computer so they load instantly. A code encrypts them, and it is forgotten after 30 minutes of not using Allo — so they cannot be read by anyone who picks this machine up.'
            : 'Allo has been idle, so the messages stored on this computer were locked. Your code unlocks them.'}
        </p>

        <label className="vg-field">
          <span>{enrolling ? 'Choose a code' : 'Code'}</span>
          <input
            type="password" inputMode="numeric" autoComplete="off" autoFocus
            value={pin} onChange={(e) => setPin(e.target.value)} disabled={busy}
          />
        </label>

        {enrolling && (
          <label className="vg-field">
            <span>Type it again</span>
            <input
              type="password" inputMode="numeric" autoComplete="off"
              value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={busy}
            />
          </label>
        )}

        {err && <div className="vg-err">{err}</div>}

        <button className="vg-go" type="submit" disabled={busy || pin.length < 4}>
          {busy ? 'Working…' : (enrolling ? 'Lock it' : 'Unlock')}
        </button>

        {enrolling && (
          <>
            <button className="vg-skip" type="button" onClick={onSkip} disabled={busy}>
              Not now
            </button>
            {/* 🟥 SAID PLAINLY, BECAUSE THE ALTERNATIVE IS SOMEBODY BELIEVING THEY
                ARE PROTECTED WHEN THEY ARE NOT — and there is no way to find out
                you were wrong about this until it matters. */}
            <p className="vg-note">
              Without a code, what Allo stores here is erased after a day of not being
              used, but until then anyone using this computer could read it.
              There is no way to recover a code you forget — Allo never sees it.
            </p>
          </>
        )}
      </form>
    </div>
  );
}
